// src/components/ProductCard.jsx
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

/* ---------------------------- Helpers ---------------------------- */

const normalizeNoAccentsLower = (s = '') =>
  String(s)
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .trim()
    .toLowerCase();

const normalizeIdUpper = (s = '') =>
  String(s || '')
    .trim()
    .toUpperCase();

const getImageList = (product) => {
  const m = Array.isArray(product?.media) ? product.media : [];
  return [m?.[0]?.url, m?.[1]?.url, m?.[2]?.url].filter(Boolean);
};

const PRODUCT_TYPE_LABELS = {
  tour: 'Circuit',
  hotel: 'Séjour',
};

// déduplication services (clé = nom de fichier icône, sinon titre normalisé)
const getServices = (product) => {
  const raw =
    product?.services ?? product?.info?.services ?? product?.info?.[0]?.services ?? [];

  const seen = new Set();
  const out = [];
  for (const s of raw) {
    const iconFile = (s?.icon || '').split('/').pop()?.toLowerCase();
    const titleNorm = normalizeNoAccentsLower(s?.title || '');
    const key = iconFile ? `i:${iconFile}` : `t:${titleNorm}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(s);
  }
  return out;
};

const capFirst = (t = '') => (t ? t.charAt(0).toUpperCase() + t.slice(1).toLowerCase() : '');

const formatDateFR = (date) => {
  if (!date) return 'Non disponible';

  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return 'Non disponible';

  return d.toLocaleDateString('fr-FR'); // jj/mm/aaaa
};

/* ---------------------------- Component ---------------------------- */

function ProductCard({ product, forceHideFrom = false }) {
  // images + fallback
  const imageList = useMemo(() => getImageList(product), [product]);
  const [imgIdx, setImgIdx] = useState(0);
  const imgSrc = imageList[imgIdx] || '/assets/img/placeholder.jpg';
  const onImgError = () => setImgIdx((i) => (i + 1 < imageList.length ? i + 1 : i));
  console.log('%csrc\b2bcomponentsProductCard.jsx:66 product', 'color: #007acc;', product);
  // drapeau
  const countryId = product.location?.country?.id?.split('_').pop()?.toLowerCase();
  const flagUrl = countryId ? `https://flagcdn.com/w160/${countryId}.png` : null;

  const isUae = countryId === 'ae' || countryId === 'uae';

  // prix
  const priceCents = product?.bestPrice?.bestPrice?.Price?._$Value ?? null;
  const priceHuman =
    priceCents != null ? `${Math.round(priceCents / 100)} €` : 'Non disponible';

  // route détail (+ passage de l’objet complet)
  const detailHref = `/b2b/produit/${product._id}`;

  /* --------- FROMS + règle “Hôtel pur” pour masquer la ville --------- */

  // bestFrom (code IATA/style _LT_XXX que la bestPrice suggère)
  const bestFrom = product?.bestPrice?.bestPrice?._$From ?? product?.bestPrice?._$From ?? null;

  // toutes les villes possibles du produit
  const froms = product?.froms ?? product?.Froms ?? [];

  // matcher bestFrom sur un élément de froms (si dispo)
  const idCore = (v) => normalizeIdUpper(v).replace(/^_?LT_/, '');
  const matchedFrom = froms.find((f) => idCore(f?.id) === idCore(bestFrom));

  // Détection “TER” très tolérante (titre "ter", id "_LT_606"/"LT_606", ou id contenant 606)
  const isTer = (f) => {
    const t = normalizeNoAccentsLower(f?.title);
    const id = normalizeIdUpper(f?.id);
    if (!t && !id) return false;
    return t === 'ter' || id === '_LT_606' || id === 'LT_606' || id.includes('606');
  };

  const hasAnyFrom = Array.isArray(froms) && froms.length > 0;
  const otherCount = (froms || []).filter((f) => !isTer(f)).length;

  // type du produit
  const ptype = normalizeNoAccentsLower(product?.productType);
  const productTypeLabel = PRODUCT_TYPE_LABELS[ptype] || product?.productType || '';

  // Hôtel pur = type hotel ET (aucune ville) OU (TER seul)
  const isHotelOnly = ptype === 'hotel' && (!hasAnyFrom || otherCount === 0);

  // si Hôtel pur → on masque la “Ville de départ”
  const shouldHideFrom = forceHideFrom || isHotelOnly;

  /* --------------------------- Services --------------------------- */

  const services = useMemo(() => getServices(product).slice(0, 15), [product]);

  /* ----------------------------- UI ------------------------------ */

  const capitalizeWords = (str = '') =>
    str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());

  return (
    <div className="mb-4">
      <Link to={detailHref} state={{ product }}>
        <div className="card-title">
          <h3 style={{ padding: '13px' }} className="text-secondary">
            {capitalizeWords((product.description?.name || '').slice(0, 35)) || 'Sans nom'}
            {product.description?.name?.length > 35 ? '…' : ''}
          </h3>
        </div>

        <div>
          <span
            className="alert alert-primary position-absolute top-1 start-0 m-2"
            style={{ zIndex: 1, padding: '.25rem 1rem' }}
          >
            {capitalizeWords(productTypeLabel)}
          </span>

          <img
            src={imgSrc}
            onError={onImgError}
            alt={product.description?.name || ''}
            style={{ width: '100%', height: '230px', objectFit: 'cover' }}
            loading="lazy"
          />
        </div>

        {flagUrl && (
          <img
            src={flagUrl}
            className="rounded-circle d-block mx-auto"
            alt="flag"
            width="52"
            height="50"
            style={{
              marginTop: '-1.5rem',
              borderRadius: '50%',
              objectFit: isUae ? undefined : 'cover',
            }}
          />
        )}

        <div className="d-flex justify-content-end">
          À partir de{' '}
          <strong>
            <h3 className="text-primary px-1" style={{ marginTop: '-2px' }}>
              {priceHuman}
              <span>/pers</span>
            </h3>
          </strong>
        </div>

        <div className="card-body pr-5 pl-5 pn-5 p-0" style={{ color: '#000' }}>
          {!shouldHideFrom && (
            <div className="d-flex">
              Ville de départ :{' '}
              <h4 style={{ color: '#000064', marginLeft: 5 }}>
                {matchedFrom ? capFirst(matchedFrom.title) : bestFrom || '—'}
              </h4>
            </div>
          )}
          <strong>{capFirst(product.location?.city?.title) || 'Non spécifiée'}</strong> <br />
          Pour <strong>{product.information?.Night}</strong> nuits <br />
          Le <strong>{formatDateFR(product.bestPrice?.startsAt)}</strong>
          <br />
          {services.length > 0 && (
            <div className="d-flex flex-wrap gap-2 mt-3 justify-content-center">
              {services.map((s, idx) => (
                <div
                  key={s.id || s._id || s.title || idx}
                  className="d-flex flex-column align-items-center gap-1 mb-3 col-3 text-center"
                >
                  <div>
                    {s.icon ? (
                      <img
                        height="35"
                        width="auto"
                        src={s.icon.startsWith('/') ? s.icon : s.icon}
                        alt=""
                        title={s.title}
                        loading="lazy"
                      />
                    ) : null}
                    <span className="d-block text-primary" style={{ fontSize: '10px' }}>
                      {s.title}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Link>
    </div>
  );
}

export default ProductCard;
